import React, { useState, useCallback, useContext, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { Stack, mergeStyles } from '@fluentui/react';
import { useImagePicker } from '@bingads-webui-campaign-react/add-image-hooks';
import { AdPreview, DisplayAdsContainer, PopulateImages, imagePlacementsToImage, getODataConfig } from '@bingads-webui-campaign-react/xandr-display-ads-campaign-shared-components';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { PerformanceMarkerContext } from '@bingads-webui-campaign-react/performance-marker-provider';
import { useFormService } from '@bingads-webui-react/form';
import { useWizardService, matchStepState, STEP_STATES } from '@bingads-webui-react/wizard';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { FormErrorGroup, UnmappedFormErrors } from '@bingads-webui-campaign-react/form-error';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { StepHeader } from '../../shared/step-header';

const adCreatorStyles = mergeStyles({
  width: 500,
});

const headerStyles = mergeStyles({
  width: 450,
});

export const DisplayAds = ({
  data,
  onDataChange,
  error,
}) => {
  const {
    errors = [],
  } = error || {};

  const { scope } = useContext(ScopeContext) || {};
  const { cid, aid } = scope.values;

  const activity = useContext(ActivityContext);

  const { state } = useWizardService();

  const { campaign = {} } = state.context.wizardData;

  const { Id: campaignId } = campaign;

  const currentStates = state.toStrings()[1];

  const i18n = useContext(I18nContext);

  const serverContext = useContext(ServerContext);
  const { Permissions, DynamicPermissions } = serverContext || { };
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);

  const perfMarker = useContext(PerformanceMarkerContext);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const oDataConfig = useMemo(() => getODataConfig(serverContext), [serverContext]);

  const { state: { context: { saveFailed } } } = useFormService();

  const {
    displayImagePicker,
    setDisplayImagePicker,
    selectedMediaPanelSource,
    setSelectedMediaPanelSource,
  } = useImagePicker();

  const removeEmptyAds = newAdsData => newAdsData.filter(ad => !_.isEmpty(ad.data));

  const onNewImageSelected = useCallback((imagePlacements) => {
    const newAds = imagePlacements.map(imagePlacement => (imagePlacementsToImage({
      crops: imagePlacement,
      defaults: {
        FinalUrls: [],
        isValid: false,
        isDirty: false,
      },
    })));
    const result = {
      data: [...newAds],
      isValid: false,
      isDirty: true,
    };
    onDataChange({ data: result, isValid: result.isValid });
    setDisplayImagePicker(false);
  }, [onDataChange, setDisplayImagePicker]);

  const onAdsValidation = useCallback((results) => {
    const { data: newAdsData, ...restResults } = results;

    const newAds = removeEmptyAds(newAdsData);

    const dataPatch = {
      data: [
        ...newAds,
      ],
      ...restResults,
    };
    onDataChange({ data: dataPatch, isValid: dataPatch.isValid });
  }, [onDataChange]);

  useEffect(() => {
    if (activity && matchStepState(state, STEP_STATES.LOADING)) {
      activity.signal(`onDisplayAdsStepLoading - ${currentStates}`);
    }

    if (activity && matchStepState(state, STEP_STATES.SAVING)) {
      activity.signal(`onDisplayAdsStepSaving - ${currentStates}`);
    }
  }, [activity, currentStates, state]);

  return (
    <div className="display-ads-step">
      <div className="basic-ads-section">
        <ActuationGroup actuationProps={{ isActive: saveFailed }}>
          <AttentionBeaconGroup>
            <FormErrorGroup formErrorProps={{ errors, showAllValidationErrors: saveFailed }}>
              <Stack>
                <div className={headerStyles}>
                  <StepHeader
                    title={_TL_('Create your Display ads')}
                    description={_TL_('Provide the details and images you want to include in your ad.')}
                  />
                </div>
                <div className="server-errors">
                  <UnmappedFormErrors />
                </div>
                <Stack horizontal tokens={{ childrenGap: 50 }}>
                  <div className={adCreatorStyles}>
                    {data && _.isEmpty(data.data) &&
                      <PopulateImages
                        aid={aid}
                        campaignId={campaignId}
                        i18n={i18n}
                        perfMarker={perfMarker}
                        permissions={permissions}
                        oDataConfig={oDataConfig}
                        serverContext={serverContext}
                        onNewImageSelected={onNewImageSelected}
                        selectedMediaPanelSource={selectedMediaPanelSource}
                        setSelectedMediaPanelSource={setSelectedMediaPanelSource}
                        displayImagePicker={displayImagePicker}
                        setDisplayImagePicker={setDisplayImagePicker}
                      />
                    }
                    {data && !_.isEmpty(data.data) &&
                      <DisplayAdsContainer
                        aid={aid}
                        cid={cid}
                        campaignId={campaignId}
                        i18n={i18n}
                        perfMarker={perfMarker}
                        onAdsValidation={onAdsValidation}
                        currentImageIndex={currentImageIndex}
                        ads={data.data}
                        oDataConfig={oDataConfig}
                        permissions={permissions}
                      />
                    }
                  </div>
                  {!_.isEmpty(data.data) &&
                    <div>
                      <AdPreview
                        ads={data}
                        i18n={i18n}
                        perfMarker={perfMarker}
                        onUpdateCurrentImageIndex={setCurrentImageIndex}
                      />
                    </div>
                  }
                </Stack>
              </Stack>
            </FormErrorGroup>
          </AttentionBeaconGroup>
        </ActuationGroup>
      </div>
    </div>

  );
};

DisplayAds.propTypes = {
  data: PropTypes.shape({
    data: PropTypes.shape({}),
  }),
  onDataChange: PropTypes.func.isRequired,
  error: PropTypes.shape({
    errors: PropTypes.arrayOf(PropTypes.shape({})),
  }),
};

DisplayAds.defaultProps = {
  data: {},
  error: {},
};
